import { ChangeEvent, useRef, useState, useReducer } from 'react';
import { Divider, FormControl, FormHelperText } from '@mui/material';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseTextField from '@/components/form/BaseTextField';
import BaseTransferList from '@/components/ui/BaseTransferList';
import localforage from 'localforage';
import { fetchStoreListAPI } from '@/api/stores';
import { CreateClusterRequest, EditClusterRequest } from '@/types/pages/admin/apiRBAC';
import { Store } from '@/types/pages/stores';
import { useAppContext } from '@/provider/contexts';
import { AdminClusterPageDialogAction, AdminClusterPageDialogState } from '@/types/pages/admin/cluster';
import { fetchClusterDetailAPI, findAllUserAPI } from '@/api/admin/rbac';
import { BaseAccount } from '@/types/pages/admin/account';
import { useTranslation } from 'react-i18next';

interface AdminClusterDialogProps {
    dialogType: 'Create' | 'Edit';
    isDialogOpen: boolean;
    editClusterId?: number | null;
    onConfirm: (data: CreateClusterRequest | EditClusterRequest, id?: number) => Promise<void>;
    onClose: () => void;
}

function reducer(state: AdminClusterPageDialogState, action: AdminClusterPageDialogAction) {
    switch (action.type) {
        case 'SET_IS_STORE_TRANSFER_LIST_LOADING':
            return { ...state, isStoreTransferListLoading: action.payload };
        case 'SET_IS_ACCOUNT_TRANSFER_LIST_LOADING':
            return { ...state, isAccountTransferListLoading: action.payload };
        case 'SET_LEFT_STORE_LIST':
            return { ...state, leftStoreList: action.payload };
        case 'SET_RIGHT_STORE_LIST':
            return { ...state, rightStoreList: action.payload };

        case 'SET_LEFT_ACCOUNT_LIST':
            return { ...state, leftAccountList: action.payload };
        case 'SET_RIGHT_ACCOUNT_LIST':
            return { ...state, rightAccountList: action.payload };
        case 'CLEAR_LEFT_AND_RIGHT_LIST':
            return {
                ...state,
                leftStoreList: [],
                rightStoreList: [],
                leftAccountList: [],
                rightAccountList: [],
            };
        default:
            return state;
    }
}

export default function AdminClusterPageDialog({
    dialogType,
    isDialogOpen,
    editClusterId,
    onConfirm,
    onClose,
}: AdminClusterDialogProps) {
    const { t } = useTranslation('common');
    const dialogTitle = dialogType === 'Create' ? t('Create Cluster') : t('Edit Cluster');
    const [validatedMessage, setValidatedMessage] = useState<{
        [key: string]: string | null;
    }>({});
    const [clusterData, setClusterData] = useState<CreateClusterRequest>({
        cid: 0,
        cluster_name: '',
        cluster_description: '',
        stores: [],
        users: [],
    });

    const editDataRef = useRef({
        cid: 0,
        cluster_name: '',
        cluster_description: '',
        stores: [] as number[],
        users: [] as number[],
    });

    const { setNotification } = useAppContext();

    const defaultState = {
        isStoreTransferListLoading: true,
        isAccountTransferListLoading: true,
        leftStoreList: [] as Store[],
        rightStoreList: [] as Store[],
        leftAccountList: [] as BaseAccount[],
        rightAccountList: [] as BaseAccount[],
    };

    const [
        {
            isStoreTransferListLoading,
            isAccountTransferListLoading,
            leftStoreList,
            rightStoreList,
            leftAccountList,
            rightAccountList,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);

    const checkIsRequired = (key: string) => {
        return key === 'cluster_name';
    };

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
        const value = e.target.value;

        setClusterData((prevData) => ({
            ...prevData,
            [key]: value ?? '',
        }));

        if (checkIsRequired(key) && !value) {
            setValidatedMessage((prevMessage) => ({
                ...prevMessage,
                [key]: t('Required'),
            }));
        } else {
            setValidatedMessage((prevMessage) => ({
                ...prevMessage,
                [key]: null,
            }));
        }
    };
    const formValidate = () => {
        const newValidatedMessage: { [key: string]: string | null } = {};
        let isFormValid = true;

        Object.keys(clusterData).forEach((key) => {
            if (checkIsRequired(key) && !clusterData[key]) {
                newValidatedMessage[key] = t('Required');
                isFormValid = false;
            }
        });

        setValidatedMessage(newValidatedMessage);
        return isFormValid;
    };

    const fetchStoreListHandler = async () => {
        dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: true });
        const corporationId = await localforage.getItem('corporation_id');
        setClusterData((prevData) => ({
            ...prevData,
            cid: corporationId as number,
        }));

        const params = {
            cid: corporationId as number,
            // f_store_name: searchQuery,
        };

        const {
            data: { data, message, isSuccess },
        } = await fetchStoreListAPI(params);

        if (isSuccess && dialogType === 'Create') {
            setCreateDialogStoreList(data.aaData);
        } else if (isSuccess && dialogType === 'Edit' && editClusterId) {
            await setEditDialogStoreList(data.aaData);
        } else {
            setNotification({ message, severity: 'error' });
        }

        dispatch({
            type: 'SET_IS_STORE_TRANSFER_LIST_LOADING',
            payload: false,
        });
    };

    const setCreateDialogStoreList = (data: Store[]) => {
        dispatch({
            type: 'SET_LEFT_STORE_LIST',
            payload: data,
        });
        dispatch({
            type: 'SET_RIGHT_STORE_LIST',
            payload: [],
        });
    };

    const setEditDialogStoreList = async (allStoreList: Store[]) => {
        if (!editDataRef.current?.stores) return;
        // Create a Map for quick lookup
        const storeIdLookup = new Map(editDataRef.current.stores.map((storeId) => [storeId, true]));

        // Filter allStoreList by checking if store_id is or not in the lookup Map
        const nonClusterStores = allStoreList.filter((store) => !storeIdLookup.has(store.store_id));

        const clusterStores = allStoreList.filter((store) => storeIdLookup.has(store.store_id));
        dispatch({
            type: 'SET_LEFT_STORE_LIST',
            payload: nonClusterStores,
        });

        dispatch({
            type: 'SET_RIGHT_STORE_LIST',
            payload: clusterStores,
        });
    };

    const updateStoreListHandler = (newLeft: Store[], newRight: Store[]) => {
        dispatch({ type: 'SET_LEFT_STORE_LIST', payload: newLeft });
        dispatch({ type: 'SET_RIGHT_STORE_LIST', payload: newRight });
    };

    const fetchAccountListHandler = async () => {
        dispatch({
            type: 'SET_IS_ACCOUNT_TRANSFER_LIST_LOADING',
            payload: true,
        });
        const corporationId = await localforage.getItem('corporation_id');
        setClusterData((prevData) => ({
            ...prevData,
            cid: corporationId as number,
        }));
        const params = {
            corporate_id: corporationId as number,
            keyword: '#ALL#',
        };

        const {
            data: { data, message, isSuccess },
        } = await findAllUserAPI(params);

        if (isSuccess && dialogType === 'Create') {
            setCreateDialogAccountList(data);
        } else if (isSuccess && dialogType === 'Edit' && editClusterId) {
            await setEditDialogAccountList(data);
        } else {
            setNotification({ message, severity: 'error' });
        }

        dispatch({
            type: 'SET_IS_ACCOUNT_TRANSFER_LIST_LOADING',
            payload: false,
        });
    };

    const setCreateDialogAccountList = (data: BaseAccount[]) => {
        dispatch({
            type: 'SET_LEFT_ACCOUNT_LIST',
            payload: data,
        });
        dispatch({
            type: 'SET_RIGHT_ACCOUNT_LIST',
            payload: [],
        });
    };

    const setEditDialogAccountList = async (allAccountList: BaseAccount[]) => {
        if (!editDataRef.current?.users) return;
        // Create a Map for quick lookup
        const accountIdLookup = new Map(editDataRef.current.users.map((userId) => [userId, true]));
        // Filter allStoreList by checking if store_id is or not in the lookup Map
        const nonClusterAccounts = allAccountList.filter((account) => !accountIdLookup.has(account.user_id));

        const clusterAccounts = allAccountList.filter((account) => accountIdLookup.has(account.user_id));
        dispatch({
            type: 'SET_LEFT_ACCOUNT_LIST',
            payload: nonClusterAccounts,
        });

        dispatch({
            type: 'SET_RIGHT_ACCOUNT_LIST',
            payload: clusterAccounts,
        });
        // } else {
        //     setNotification({ message, severity: 'error' });
        // }
    };

    const updateAccountListHandler = (newLeft: BaseAccount[], newRight: BaseAccount[]) => {
        dispatch({ type: 'SET_LEFT_ACCOUNT_LIST', payload: newLeft });
        dispatch({ type: 'SET_RIGHT_ACCOUNT_LIST', payload: newRight });
    };

    const fetchClusterDetail = async () => {
        if (!editClusterId) return;
        dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: true });
        dispatch({
            type: 'SET_IS_ACCOUNT_TRANSFER_LIST_LOADING',
            payload: true,
        });
        const corporationId = await localforage.getItem('corporation_id');
        const params = {
            cid: corporationId as number,
        };

        const {
            data: { data, message, isSuccess },
        } = await fetchClusterDetailAPI(editClusterId, params);

        if (isSuccess) {
            const cluster = {
                id: data.id,
                cluster_name: data.cluster_name,
                cluster_description: data.cluster_description,
                stores: data.stores.map((store) => store.id),
                users: data.users.map((user) => user.id),
            };
            setClusterData((prevData) => ({
                ...prevData,
                ...cluster,
                cid: corporationId as number,
            }));
            editDataRef.current = { ...cluster, cid: corporationId as number };
        } else {
            setNotification({ message, severity: 'error' });
        }
    };

    const confirmHandler = async () => {
        if (!formValidate()) return;
        const storeIds = rightStoreList.map((store) => store.store_id);
        const accountIds = rightAccountList.map((account) => account.user_id);

        const data = {
            ...clusterData,
            stores: storeIds,
            users: accountIds,
        };

        if (dialogType === 'Create') {
            onConfirm(data);
        }

        if (dialogType === 'Edit') {
            onConfirm(data, editClusterId as number);
        }
    };

    return (
        <BaseDialog
            open={isDialogOpen}
            maxWidth="md"
            dialogTitle={dialogTitle}
            confirmButtonText={t('Save')}
            cancelButtonText={t('Cancel')}
            disableConfirm={isStoreTransferListLoading || isAccountTransferListLoading}
            showSpinnerOnDisable={false}
            onConfirm={confirmHandler}
            onClose={onClose}
            TransitionProps={{
                onEnter: async () => {
                    const fetchDetailAndListData = async () => {
                        await fetchClusterDetail();
                        fetchStoreListHandler();
                        fetchAccountListHandler();
                    };
                    if (isDialogOpen && dialogType === 'Create') {
                        fetchStoreListHandler();
                        fetchAccountListHandler();
                    }

                    if (isDialogOpen && dialogType === 'Edit') {
                        fetchDetailAndListData();
                    }
                },
                onExited: () => {
                    setValidatedMessage({});
                    setClusterData({
                        cid: 0,
                        cluster_name: '',
                        cluster_description: '',
                        stores: [],
                        users: [],
                    });
                    editDataRef.current = {
                        cid: 0,
                        cluster_name: '',
                        cluster_description: '',
                        stores: [],
                        users: [],
                    };
                    dispatch({
                        type: 'CLEAR_LEFT_AND_RIGHT_LIST',
                    });
                },
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.6rem',
                    width: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.3rem',
                        width: '100%',
                    }}
                >
                    <FormControl error={!!validatedMessage['cluster_name']} sx={{ mt: 0.7 }}>
                        <BaseTextField
                            label={t('Cluster Name')}
                            required={checkIsRequired('cluster_name')}
                            error={!!validatedMessage.cluster_name}
                            value={clusterData?.cluster_name || ''}
                            onChange={(e) => inputChangeHandler(e, 'cluster_name')}
                        />
                        <FormHelperText>{validatedMessage.cluster_name}</FormHelperText>
                    </FormControl>
                    <FormControl error={!!validatedMessage['cluster_description']}>
                        <BaseTextField
                            label={t('Cluster Description')}
                            error={!!validatedMessage.cluster_description}
                            value={clusterData?.cluster_description || ''}
                            onChange={(e) => inputChangeHandler(e, 'cluster_description')}
                        />
                        <FormHelperText>{validatedMessage.cluster_description}</FormHelperText>
                    </FormControl>
                </div>
                <BaseTransferList
                    leftList={leftStoreList}
                    rightList={rightStoreList}
                    idKey="store_id"
                    labelKey="store_name"
                    isLoading={isStoreTransferListLoading}
                    listsTitle={{ left: t('Store List'), right: t('Cluster Stores') }}
                    onUpdateLists={updateStoreListHandler}
                />
                <Divider sx={{ mt: 2, mb: 1 }} />
                <BaseTransferList
                    leftList={leftAccountList}
                    rightList={rightAccountList}
                    idKey="user_id"
                    labelKey="username"
                    isLoading={isAccountTransferListLoading}
                    listsTitle={{
                        left: t('Account List'),
                        right: t('Cluster Accounts'),
                    }}
                    onUpdateLists={updateAccountListHandler}
                />
            </div>
        </BaseDialog>
    );
}
